import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePublicBanners } from '@/hooks';

interface SiteBannerCarouselProps {
  position?: string;
  autoPlayInterval?: number;
  className?: string;
  compact?: boolean;
  /** Outra posição cujos banners entram no mesmo carrossel (ex.: o topo no Início). */
  juntarCom?: string;
}

export function SiteBannerCarousel({
  position = 'home',
  autoPlayInterval = 5000,
  className,
  compact = false,
  juntarCom,
}: SiteBannerCarouselProps) {
  const { data: banners, isLoading } = usePublicBanners(position);
  // sem `juntarCom` a segunda consulta é a mesma da primeira (vem do cache)
  const { data: extras } = usePublicBanners(juntarCom ?? position);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const items = [
    ...(juntarCom && Array.isArray(extras) ? extras : []),
    ...(Array.isArray(banners) ? banners : []),
  ];

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  // Auto-play
  useEffect(() => {
    if (!isHovered && items.length > 1) {
      const interval = setInterval(goToNext, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [isHovered, items.length, autoPlayInterval, goToNext]);

  // Reset index when items change
  useEffect(() => {
    setCurrentIndex(0);
  }, [items.length]);

  if (isLoading || items.length === 0) return null;

  const currentItem = items[currentIndex];

  const Wrapper = currentItem.linkUrl ? 'a' : 'div';
  const wrapperProps = currentItem.linkUrl
    ? {
        href: currentItem.linkUrl,
        target: '_blank' as const,
        rel: 'noopener noreferrer',
      }
    : {};

  return (
    <div
      className={cn(
        'group relative mb-4 min-w-0 overflow-hidden rounded-xl',
        compact
          ? 'h-[140px] sm:h-[160px]'
          : // na proporção da arte (1920×520, lib/image-sizes.ts): altura fixa
            // cortava o texto que vem desenhado nela
            'aspect-[1920/520] min-h-[110px] w-full',
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Wrapper
        {...wrapperProps}
        className="relative block h-full w-full cursor-pointer"
      >
        <img
          src={currentItem.imageUrl}
          alt={currentItem.title || 'Banner'}
          className="group- h-full w-full object-cover transition-transform duration-700"
          fetchPriority="high"
          decoding="async"
          loading="eager"
        />

        {/*
          O título NÃO é escrito por cima (29/10): o banner é uma arte com o
          texto desenhado nela, como os do seed, e o título em branco no pé
          brigava com a arte ("tem um texto bosta"). O título do cadastro
          segue como texto alternativo. Saiu junto o escurecido do pé, que só
          existia para esse título.
        */}
      </Wrapper>

      {/* Navigation Arrows */}
      {items.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              goToPrevious();
            }}
            className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-all hover:bg-black/75 group-hover:opacity-100 sm:left-5 sm:h-12 sm:w-12"
            aria-label="Anterior"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              goToNext();
            }}
            className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-all hover:bg-black/75 group-hover:opacity-100 sm:right-5 sm:h-12 sm:w-12"
            aria-label="Próximo"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </>
      )}

      {/* Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                'h-2 rounded-full transition-all',
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/75',
              )}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
